import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreakdownTabContext } from '../../contexts/BreakdownTabContext';
import { useBudgetTabContext } from '../../contexts/BudgetTabContext';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import { useFilmContext } from '../../contexts/FilmContext';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import PermissionUserSearch from '../PermissionUserSearch/PermissionUserSearch';
import {
  getPermissionsByResource,
  GranteeType,
  PermissionRole,
  ResourceType,
  revokeUserPermission,
  setUserPermission,
  UserPermissions,
} from '../../api/userPermissions';
import { getCurrentUserID } from '../../utils/userUtils';
import { updateBreakdown } from '../../api/breakdown';
import { updateSchedule } from '../../api/schedule';
import ExportDialog, { EXPORT_DIALOG_WIDTH_PX } from '../Export/ExportDialog';
import FindReplace from '../FindReplace/FindReplace';
import { useMultiColumnFindReplace } from '../FindReplace/useMultiColumnFindReplace';
import { useElementFindReplace } from '../FindReplace/useElementFindReplace';
import './TabBar.css';
import budgetYellowIcon from '../../assets/images/TabBar/budget_yellow.png';
import budgetWhiteIcon from '../../assets/images/TabBar/budget_white.png';
// 导入 SVG 图标作为 React 组件
import { ReactComponent as HomeIcon } from '../../assets/svg/TabBar/home.svg';
import { ReactComponent as DownloadIcon } from '../../assets/svg/TabBar/download.svg';
import { ReactComponent as ShareIcon } from '../../assets/svg/TabBar/share.svg';
import { ReactComponent as BreakdownIcon } from '../../assets/svg/TabBar/breakdown.svg';
import { ReactComponent as ScheduleIcon } from '../../assets/svg/TabBar/schedule.svg';
import { ReactComponent as ItemIcon } from '../../assets/svg/TabBar/item.svg';
import { ReactComponent as BudgetIcon } from '../../assets/svg/TabBar/budget.svg';
import { ReactComponent as FinancialIcon } from '../../assets/svg/TabBar/Financial.svg';
import { ReactComponent as AnalysisIcon } from '../../assets/svg/TabBar/Analysis.svg';
import { ReactComponent as FindReplaceIcon } from '../../assets/svg/Breakdown/findreplace.svg';

// 检查是否在 Windows 上的 Electron 环境中
const isWindowsElectron = typeof window !== 'undefined' && 
  window.require && 
  window.require('electron') && 
  typeof process !== 'undefined' && 
  process.platform === 'win32';

// 检查是否在 macOS 上的 Electron 环境中
const isMacElectron = typeof window !== 'undefined' && 
  window.require && 
  window.require('electron') && 
  typeof process !== 'undefined' && 
  process.platform === 'darwin';

interface TabItem {
  id: string;
  title: string;
  active: boolean;
  fixed?: boolean;
}

interface UserInfo {
  name: string;
  avatar: string;
}

interface TabBarProps {
  tabs: TabItem[];
  user: UserInfo;
  onTabClick: (id: string) => void;
  onTabClose: (id: string) => void;
  currentBreakdownId?: string | null; // 当前选中的分场ID
  isHomePage?: boolean; // 是否在主页
  currentPath?: string; // 当前路径
}

// 单个标签项组件，使用memo优化
const TabItemComponent: React.FC<{
  tab: TabItem;
  onTabClick: (id: string) => void;
  onTabClose: (id: string) => void;
}> = React.memo(({ tab, onTabClick, onTabClose }) => {
  // 判断是否是预算创建标签
  const isBudgetTab = (id: string) => id.startsWith('create-budget-');
  const isHomeTab = tab.id === 'home';

  return (
    <div 
      className={`tab ${tab.active ? 'active' : ''} ${isHomeTab ? 'home-tab' : ''}`}
      onClick={() => onTabClick(tab.id)}
    >
      <div className="tab-left"></div>
      <div className={`tab-mid ${isHomeTab ? 'home-tab-mid' : ''}`}>
        {isHomeTab && (
          <HomeIcon className="tab-icon" aria-label="主页" />
        )}
        {isBudgetTab(tab.id) && (
          <img 
            src={tab.active ? budgetWhiteIcon : budgetYellowIcon} 
            alt="预算" 
            className="tab-icon" 
          />
        )}
        <span className="tab-title">{tab.title}</span>
        {!tab.fixed && (
          <button 
            className="tab-close"
            onClick={(e) => {
              e.stopPropagation();
              onTabClose(tab.id);
            }}
            tabIndex={-1}
          >
            ×
          </button>
        )}
      </div>
      <div className="tab-right"></div>
    </div>
  );
});

const TabBar: React.FC<TabBarProps> = React.memo(({ tabs, user, onTabClick, onTabClose, currentBreakdownId, isHomePage = false, currentPath = '' }) => {
  const { t, i18n } = useTranslation();
  const { breakdownId: contextBreakdownId, activeTab } = useBreakdownTabContext();
  const { budgetId, activeTab: budgetActiveTab } = useBudgetTabContext();
  const { getBreakdownCache, setBreakdownCache, getElementCache, getScheduleCache, setScheduleCache, subscribeBreakdownCache } = useDataCache();
  const { selectedFilmName } = useFilmContext();
  const { serverSyncedData } = useCurrentUser();
  const [exportMenuVisible, setExportMenuVisible] = useState(false);
  const [permissionSearchVisible, setPermissionSearchVisible] = useState(false);
  const [resourcePermissions, setResourcePermissions] = useState<UserPermissions[]>([]);
  const [currentBreakdownOwnerID, setCurrentBreakdownOwnerID] = useState<string | null>(null);
  const exportButtonRef = useRef<HTMLButtonElement>(null);
  const findReplaceButtonRef = useRef<HTMLButtonElement>(null);
  
  // 固定的三个标签 - 需要先定义，因为后面的函数会用到
  const breakdownIdForTabs = contextBreakdownId || currentBreakdownId;

  useEffect(() => {
    if (!breakdownIdForTabs) {
      setCurrentBreakdownOwnerID(null);
      return;
    }
    const updateOwner = () => {
      setCurrentBreakdownOwnerID(
        getBreakdownCache(breakdownIdForTabs)?.breakdownInfo?.breakdownOwnerID || null
      );
    };
    updateOwner();
    return subscribeBreakdownCache(breakdownIdForTabs, updateOwner);
  }, [breakdownIdForTabs, getBreakdownCache, subscribeBreakdownCache]);

  const permissionResource = useMemo(() => {
    if (!breakdownIdForTabs) return null;
    const breakdownInfo = getBreakdownCache(breakdownIdForTabs)?.breakdownInfo;
    if (activeTab === 'breakdown' || activeTab === 'element-manager') {
      return {
        type: ResourceType.BREAKDOWN,
        id: breakdownIdForTabs,
        ownerID: currentBreakdownOwnerID || breakdownInfo?.breakdownOwnerID || null,
      };
    }
    if (activeTab !== 'schedule') return null;
    const scheduleCache = getScheduleCache(breakdownIdForTabs);
    const schedules = scheduleCache?.schedules || [];
    const rememberedScheduleID = Array.isArray(serverSyncedData.userLastUsedScheduleID)
      ? serverSyncedData.userLastUsedScheduleID.find((item) => item.startsWith(`${breakdownIdForTabs};`))?.split(';')[1]
      : null;
    const scheduleID = scheduleCache?.currentScheduleId || scheduleCache?.currentScheduleInfo?.scheduleID ||
      rememberedScheduleID || [...schedules].sort((a, b) =>
        new Date(b.scheduleUpdateTime || 0).getTime() - new Date(a.scheduleUpdateTime || 0).getTime()
      )[0]?.scheduleID;
    const schedule = schedules.find((item) => item.scheduleID === scheduleID) || scheduleCache?.currentScheduleInfo;
    return scheduleID ? {
      type: ResourceType.SCHEDULE,
      id: scheduleID,
      ownerID: schedule?.scheduleOwnerID || null,
    } : null;
  }, [activeTab, breakdownIdForTabs, currentBreakdownOwnerID, getBreakdownCache, getScheduleCache, serverSyncedData.userLastUsedScheduleID]);

  useEffect(() => {
    if (!permissionResource) {
      setResourcePermissions([]);
      setPermissionSearchVisible(false);
      return;
    }
    let cancelled = false;
    void getPermissionsByResource(permissionResource.type, permissionResource.id)
      .then((response) => {
        if (!cancelled) setResourcePermissions(response.data || []);
      })
      .catch((error) => {
        if (!cancelled) console.error('加载当前资源权限失败:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [permissionResource]);

  const currentUserID = getCurrentUserID();
  const currentUserOwnsResource = Boolean(
    permissionResource?.ownerID && permissionResource.ownerID === currentUserID
  );
  const currentResourceRole = resourcePermissions.find(
    (permission) =>
      permission.granteeType === GranteeType.USER && permission.granteeID === currentUserID
  )?.role;
  const canManageResourceMembers =
    currentUserOwnsResource || currentResourceRole === PermissionRole.ADMIN;
  const canEditResource =
    currentUserOwnsResource ||
    currentResourceRole === PermissionRole.ADMIN ||
    currentResourceRole === PermissionRole.EDITOR;

  const openPermissionSearch = useCallback(async () => {
    if (!permissionResource) return;
    setPermissionSearchVisible((visible) => !visible);
    try {
      const response = await getPermissionsByResource(permissionResource.type, permissionResource.id);
      setResourcePermissions(response.data || []);
    } catch (error) {
      console.error('加载协作者权限失败:', error);
    }
  }, [permissionResource]);

  const transferResourceOwner = useCallback(async (
    userID: string,
    previousOwnerRole: Exclude<PermissionRole, PermissionRole.OWNER> | 'remove',
    removeAllCollaborators: boolean,
  ) => {
    if (!permissionResource || !breakdownIdForTabs) return;
    try {
      const previousOwnerID = permissionResource.ownerID;
      const permissionsToRemove = removeAllCollaborators
        ? resourcePermissions
        : resourcePermissions.filter(permission => permission.granteeID === userID);
      await Promise.all(permissionsToRemove.map(permission => revokeUserPermission({
        resourceType: permissionResource.type,
        resourceID: permissionResource.id,
        granteeType: permission.granteeType,
        granteeID: permission.granteeID,
      })));
      let previousOwnerPermission: UserPermissions | null = null;
      if (previousOwnerID && previousOwnerRole !== 'remove') {
        const permissionResponse = await setUserPermission({
          resourceType: permissionResource.type,
          resourceID: permissionResource.id,
          granteeType: GranteeType.USER,
          granteeID: previousOwnerID,
          role: previousOwnerRole,
          creatorID: previousOwnerID,
        });
        previousOwnerPermission = permissionResponse.data;
      }
      if (permissionResource.type === ResourceType.BREAKDOWN) {
        const response = await updateBreakdown(permissionResource.id, { breakdownOwnerID: userID });
        setBreakdownCache(breakdownIdForTabs, { breakdownInfo: response.data });
        setCurrentBreakdownOwnerID(userID);
      } else {
        const response = await updateSchedule(permissionResource.id, { scheduleOwnerID: userID });
        const cache = getScheduleCache(breakdownIdForTabs);
        setScheduleCache(breakdownIdForTabs, {
          schedules: (cache?.schedules || []).map(schedule =>
            schedule.scheduleID === permissionResource.id ? response.data : schedule
          ),
          ...(cache?.currentScheduleInfo?.scheduleID === permissionResource.id && {
            currentScheduleInfo: response.data,
          }),
        });
      }
      setResourcePermissions([
        ...(removeAllCollaborators
          ? []
          : resourcePermissions.filter(permission => permission.granteeID !== userID && permission.granteeID !== previousOwnerID)),
        ...(previousOwnerPermission ? [previousOwnerPermission] : []),
      ]);
      setPermissionSearchVisible(false);
    } catch (error) {
      console.error('变更拥有者失败:', error);
    }
  }, [breakdownIdForTabs, getScheduleCache, permissionResource, resourcePermissions, setBreakdownCache, setScheduleCache]);

  const saveResourcePermission = useCallback(async (
    userID: string,
    role: Exclude<PermissionRole, PermissionRole.OWNER>,
  ) => {
    if (!permissionResource) return;
    try {
      const response = await setUserPermission({
        resourceType: permissionResource.type,
        resourceID: permissionResource.id,
        granteeType: GranteeType.USER,
        granteeID: userID,
        role,
        creatorID: getCurrentUserID(),
      });
      setResourcePermissions((current) => [
        ...current.filter((permission) => permission.granteeID !== userID),
        response.data,
      ]);
    } catch (error) {
      console.error('保存协作者权限失败:', error);
    }
  }, [permissionResource]);

  const removeResourcePermission = useCallback(async (granteeID: string) => {
    if (!permissionResource) return;
    try {
      await revokeUserPermission({
        resourceType: permissionResource.type,
        resourceID: permissionResource.id,
        granteeType: GranteeType.USER,
        granteeID,
      });
      setResourcePermissions((current) =>
        current.filter((permission) => permission.granteeID !== granteeID)
      );
    } catch (error) {
      console.error('移除协作者失败:', error);
    }
  }, [permissionResource]);
  
  // 获取当前分场的数据
  const getCurrentBreakdownData = useCallback(() => {
    if (!breakdownIdForTabs) return { scenes: [], breakdownInfo: null };

    const cache = getBreakdownCache(breakdownIdForTabs);
    return {
      scenes: cache?.scenes || [],
      breakdownInfo: cache?.breakdownInfo || null
    };
  }, [breakdownIdForTabs, getBreakdownCache]);
  
  // 获取路径标题文本
  const [pathTitle, setPathTitle] = useState('');
  
  // 更新路径标题的函数
  const updatePathTitle = useCallback(() => {
    // 只有在非主页且是三个标签页之一时才显示路径标题
    if (isHomePage) {
      setPathTitle('');
      return false; // 返回 false 表示不需要继续轮询
    }
    
    // 检查是否在三个标签页之一：分场分景、拍摄计划、元素管理
    const isInBreakdownTab =
      activeTab === 'breakdown' || activeTab === 'schedule' || activeTab === 'element-manager';
    if (!isInBreakdownTab) {
      setPathTitle('');
      return false;
    }
    
    if (!breakdownIdForTabs || !selectedFilmName) {
      setPathTitle('');
      return false;
    }
    
    const breakdownInfo = getCurrentBreakdownData().breakdownInfo;
    const breakdownName = breakdownInfo?.breakdownName || '';
    if (!breakdownName) {
      setPathTitle('');
      return true; // 返回 true 表示需要继续轮询（数据还没准备好）
    }

    let nextPathTitle = `《${selectedFilmName}》${t('common.pipeSeparator')}${t('schedule.breakdownVersionLabel')}${breakdownName}`;

    if (activeTab === 'element-manager' && breakdownIdForTabs) {
      const scheduleSlice = getScheduleCache(breakdownIdForTabs);
      const schedulesList = scheduleSlice?.schedules || [];
      if (schedulesList.length === 0) {
        setPathTitle('');
        return true;
      }

      const latestScheduleId =
        schedulesList.length > 0
          ? [...schedulesList].sort((a, b) => {
              const timeA = a.scheduleUpdateTime ? new Date(a.scheduleUpdateTime).getTime() : 0;
              const timeB = b.scheduleUpdateTime ? new Date(b.scheduleUpdateTime).getTime() : 0;
              return timeB - timeA;
            })[0]?.scheduleID
          : null;

      const selectedScheduleId = Array.isArray(serverSyncedData.userLastUsedScheduleID)
        ? (serverSyncedData.userLastUsedScheduleID.find((item) => {
            const parts = item.split(';');
            return parts.length >= 2 && parts[0] === breakdownIdForTabs;
          })?.split(';')[1] ?? latestScheduleId)
        : latestScheduleId;

      if (!selectedScheduleId) {
        setPathTitle('');
        return true;
      }

      const currentSchedule = schedulesList.find((s) => s.scheduleID === selectedScheduleId);
      const currentScheduleName = currentSchedule?.scheduleName || t('scheduleList.untitledSchedule');
      nextPathTitle += `${t('common.pipeSeparator')}${t('elementManager.currentSchedule')}${currentScheduleName}`;
    }

    setPathTitle(nextPathTitle);
    return false; // 返回 false 表示数据已准备好，不需要继续轮询
  }, [isHomePage, activeTab, breakdownIdForTabs, selectedFilmName, getCurrentBreakdownData, getScheduleCache, serverSyncedData.userLastUsedScheduleID, t]);
  
  // 监听相关状态变化，更新路径标题（分场/拍摄计划/元素管理下标题所需信息不会因页内编辑而变，不需订阅缓存）
  useEffect(() => {
    // 立即尝试更新路径标题
    const needsPolling = updatePathTitle();
    
    // 如果数据还没准备好，设置轮询机制（最多轮询2秒，每100ms检查一次）
    let pollingInterval: NodeJS.Timeout | undefined;
    if (needsPolling) {
      let attempts = 0;
      const maxAttempts = 20; // 最多尝试20次（2秒）
      
      pollingInterval = setInterval(() => {
        attempts++;
        const stillNeedsPolling = updatePathTitle();
        
        // 如果数据已准备好或达到最大尝试次数，停止轮询
        if (!stillNeedsPolling || attempts >= maxAttempts) {
          clearInterval(pollingInterval);
        }
      }, 100); // 每100ms检查一次
    }
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [breakdownIdForTabs, selectedFilmName, activeTab, isHomePage, updatePathTitle]);

  useEffect(() => {
    if (!breakdownIdForTabs || isHomePage) return;
    return subscribeBreakdownCache(breakdownIdForTabs, updatePathTitle);
  }, [breakdownIdForTabs, isHomePage, subscribeBreakdownCache, updatePathTitle]);

  // 获取当前分场的所有场次元素
  const getElements = useCallback(() => {
    if (!breakdownIdForTabs) return [];
    const cache = getElementCache(breakdownIdForTabs);
    return cache?.elements || [];
  }, [breakdownIdForTabs, getElementCache]);

  // 使用多列查找替换 hook（分场分景）
  const {
    findReplaceVisible,
    findReplacePosition,
    setFindReplaceVisible,
    setFindReplacePosition,
    handleCloseFindReplace,
    getMultiColumnData,
    handleFindReplaceUpdate
  } = useMultiColumnFindReplace(
    () => getCurrentBreakdownData().scenes, // 获取最新的场次行数据
    (updatedScenes, updatedDisplayTexts) => {
      // 注意：场景数据的更新由 useMultiColumnFindReplace 内部的 useSceneUpdates hook 处理
      // 这里只更新本地状态（乐观更新），实际的服务器更新和缓存更新由 hook 自动处理
      // 如果需要更新本地状态，可以通过订阅 BreakdownDataCacheContext 来实现
    },
    undefined, // setSceneLocationDisplayTexts 不再需要，由 BreakdownDataCacheContext 统一管理
    breakdownIdForTabs || undefined
  );

  // 场次元素查找替换 hook（元素管理）
  const {
    findReplaceVisible: elementFindReplaceVisible,
    findReplacePosition: elementFindReplacePosition,
    setFindReplaceVisible: setElementFindReplaceVisible,
    setFindReplacePosition: setElementFindReplacePosition,
    handleCloseFindReplace: handleCloseElementFindReplace,
    getElementColumnData,
    handleFindReplaceUpdate: handleElementFindReplaceUpdate
  } = useElementFindReplace(getElements, breakdownIdForTabs || undefined);

  // 稳定化 columnData 和 rowIdentifiers 引用，避免 FindReplace 因父组件重渲染导致无限循环
  const elementCache = breakdownIdForTabs ? getElementCache(breakdownIdForTabs) : null;
  const breakdownCache = breakdownIdForTabs ? getBreakdownCache(breakdownIdForTabs) : null;
  const elementColumnData = useMemo(
    () => (elementCache || breakdownIdForTabs ? getElementColumnData() : []),
    [getElementColumnData, elementCache, breakdownIdForTabs]
  );
  const elementRowIdentifiers = useMemo(
    () => (elementCache || breakdownIdForTabs ? getElements().map(item => item.elementName || item.elementID || '') : []),
    [getElements, elementCache, breakdownIdForTabs]
  );
  const multiColumnData = useMemo(
    () => (breakdownCache || breakdownIdForTabs ? getMultiColumnData() : []),
    [getMultiColumnData, breakdownCache, breakdownIdForTabs]
  );
  const currentBreakdownData = useMemo(
    () => (breakdownCache || breakdownIdForTabs ? getCurrentBreakdownData() : { scenes: [], breakdownInfo: null }),
    [getCurrentBreakdownData, breakdownCache, breakdownIdForTabs]
  );

  // 切换标签时关闭查找替换弹窗
  useEffect(() => {
    setFindReplaceVisible(false);
    setElementFindReplaceVisible(false);
  }, [activeTab, setFindReplaceVisible, setElementFindReplaceVisible]);

  useEffect(() => {
    // 在 Windows 上的 Electron 环境中，给 body 添加类名
    if (isWindowsElectron) {
      document.body.classList.add('windows-electron');
    } else {
      document.body.classList.remove('windows-electron');
    }
    
    // 在 macOS 上的 Electron 环境中，给 body 添加类名
    if (isMacElectron) {
      document.body.classList.add('mac-electron');
    } else {
      document.body.classList.remove('mac-electron');
    }
    
    // 清理函数：组件卸载时移除类名
    return () => {
      document.body.classList.remove('windows-electron');
      document.body.classList.remove('mac-electron');
    };
  }, []);

  // 获取主页标签
  const homeTab = tabs.find(tab => tab.id === 'home');

  // 分场分景的固定三个标签
  const breakdownFixedTabs = [
    { id: 'breakdown-tab', title: t('tabBar.breakdownAndSets'), path: '/breakdown' },
    { id: 'schedule-tab', title: t('tabBar.schedule'), path: '/schedule' },
    { id: 'element-manager-tab', title: t('tabBar.elementManager'), path: '/elements' },
  ];

  // 预算的固定三个标签
  const budgetFixedTabs = [
    { id: 'budget-tab', title: t('tabBar.budgetTable'), path: '/budgets' },
    { id: 'financial-tab', title: t('tabBar.financial'), path: '/budgets' },
    { id: 'analysis-tab', title: t('tabBar.analysis'), path: '/budgets' }
  ];

  // 获取当前激活的分场分景标签：优先按 URL，再按 Context（避免异步同步前几帧错位）
  const getBreakdownActiveTabId = () => {
    if (currentPath.startsWith('/schedule/')) return 'schedule-tab';
    if (currentPath.startsWith('/elements/')) return 'element-manager-tab';
    if (/\/breakdown\/[^/]+/.test(currentPath)) return 'breakdown-tab';
    if (!contextBreakdownId && !currentBreakdownId) return null;
    if (activeTab === 'breakdown') return 'breakdown-tab';
    if (activeTab === 'schedule') return 'schedule-tab';
    if (activeTab === 'element-manager') return 'element-manager-tab';
    return 'breakdown-tab';
  };

  // 获取当前激活的预算标签（从 Context 获取）
  const getBudgetActiveTabId = () => {
    if (!budgetId) return null;
    // 从 Context 获取激活标签
    if (budgetActiveTab === 'budget') return 'budget-tab';
    if (budgetActiveTab === 'financial') return 'financial-tab';
    if (budgetActiveTab === 'analysis') return 'analysis-tab';
    return 'budget-tab'; // 默认激活预算表
  };

  const breakdownActiveTabId = getBreakdownActiveTabId();
  const budgetActiveTabId = getBudgetActiveTabId();

  // 处理分场分景固定标签点击
  const handleBreakdownFixedTabClick = (tabId: string) => {
    // 使用 contextBreakdownId 或 currentBreakdownId，确保有 breakdownId 时才允许点击
    if (!breakdownIdForTabs) return;
    
    // 直接调用 onTabClick，让 Layout 处理标签切换
    onTabClick(tabId);
  };

  // 处理预算固定标签点击
  const handleBudgetFixedTabClick = (tabId: string) => {
    // 确保有 budgetId 时才允许点击
    if (!budgetId) return;
    
    // 直接调用 onTabClick，让 Layout 处理标签切换
    onTabClick(tabId);
  };

  // 处理导出按钮点击
  const handleExportClick = () => {
    if (!breakdownIdForTabs) return;
    setExportMenuVisible(!exportMenuVisible);
  };

  // 获取导出菜单位置
  const getExportMenuPosition = () => {
    if (!exportButtonRef.current) {
      return { top: 36, left: 0 };
    }
    const rect = exportButtonRef.current.getBoundingClientRect();
    const menuWidth = EXPORT_DIALOG_WIDTH_PX;
    // 计算按钮中心位置，然后减去菜单宽度的一半，使菜单居中对齐
    const buttonCenterX = rect.left + rect.width / 2;
    const menuLeft = buttonCenterX - menuWidth / 2;
    
    return {
      top: rect.bottom + 3,
      left: menuLeft
    };
  };

  return (
    <div className="tab-bar">
      <div className="tab-bar-left">
        {/* <div className="tab-bar-logo">
          <img src={logoImg} alt="LINEUP" className="tab-bar-logo-image" />
        </div> */}
        {homeTab && (
          <>
            <TabItemComponent 
              key={homeTab.id}
              tab={homeTab}
              onTabClick={onTabClick}
              onTabClose={onTabClose}
            />
            {pathTitle && (
              <div className="tab-bar-path-title">
                {pathTitle}
              </div>
            )}
          </>
        )}
      </div>
      <div className="tab-bar-middle">
        <div className="tab-bar-middle-messages">
          {/* 消息区域 - 靠左对齐 */}
        </div>
        {!isHomePage && (
          <div className="tab-bar-middle-options">
            {/* 操作按钮区域 - 靠右对齐 */}
            {canEditResource && (activeTab === 'breakdown' || activeTab === 'element-manager') && breakdownIdForTabs && (
              <button 
                ref={findReplaceButtonRef}
                className="tab-bar-action-btn" 
                data-action="findreplace"
                onClick={() => {
                  if (findReplaceButtonRef.current) {
                    const rect = findReplaceButtonRef.current.getBoundingClientRect();
                    const findReplaceWidth = 260;
                    const position = {
                      top: rect.bottom + 4,
                      left: rect.left - findReplaceWidth + rect.width
                    };
                    if (activeTab === 'breakdown') {
                      setFindReplacePosition(position);
                      setFindReplaceVisible(!findReplaceVisible);
                    } else {
                      setElementFindReplacePosition(position);
                      setElementFindReplaceVisible(!elementFindReplaceVisible);
                    }
                  }
                }}
              >
                <FindReplaceIcon className="tab-bar-action-icon" aria-label={t('tabBar.findReplace')} />
                <span className="tab-bar-action-text">{t('tabBar.findReplace')}</span>
              </button>
            )}
            <button 
              ref={exportButtonRef}
              className="tab-bar-action-btn" 
              data-action="export"
              onClick={handleExportClick}
            >
              <DownloadIcon className="tab-bar-action-icon" aria-label={t('tabBar.export')} />
              <span className="tab-bar-action-text">{t('tabBar.export')}</span>
            </button>
            {(activeTab === 'breakdown' || activeTab === 'schedule') && (
              <>
                {canManageResourceMembers && <div className="tab-bar-permission-action">
                  <button
                    type="button"
                    className={`tab-bar-action-btn${permissionSearchVisible ? ' is-active' : ''}`}
                    data-action="invite-share"
                    onClick={() => void openPermissionSearch()}
                  >
                    <ShareIcon className="tab-bar-action-icon" aria-label={t('tabBar.inviteShare')} />
                    <span className="tab-bar-action-text">{t('tabBar.inviteShare')}</span>
                  </button>
                  {permissionResource && (
                    <PermissionUserSearch
                      ownerID={permissionResource.ownerID}
                      permissions={resourcePermissions}
                      visible={permissionSearchVisible}
                      onClose={() => setPermissionSearchVisible(false)}
                      onChange={(userID, role) => {
                        if (userID && role) void saveResourcePermission(userID, role);
                      }}
                      onRoleChange={(permission, role) => {
                        void saveResourcePermission(permission.granteeID, role);
                      }}
                      onRemove={(permission) => {
                        void removeResourcePermission(permission.granteeID);
                      }}
                      onTransferOwnership={currentUserOwnsResource
                        ? (userID, previousRole, removeAll) => void transferResourceOwner(userID, previousRole, removeAll)
                        : undefined}
                    />
                  )}
                </div>}
              </>
            )}
          </div>
        )}
      </div>
      <div className="tab-bar-right">
        {/* 判断应该显示哪个标签组：根据路径判断 */}
        {/* 如果路径是 /breakdown 相关，显示分场分景固定标签 */}
        {!isHomePage &&
          (currentPath.startsWith('/breakdown') ||
            currentPath.startsWith('/elements') ||
            currentPath.startsWith('/element-manager') ||
            currentPath.startsWith('/schedule')) && (
          <div
            className={`tabs${(i18n.language || '').startsWith('en') ? ' tabs--breakdown-fixed-en' : ''}`}
          >
            {breakdownFixedTabs.map(tab => {
              const isActive = breakdownActiveTabId === tab.id;
              let IconComponent: React.FC<React.SVGProps<SVGSVGElement>> | null = null;
              let iconAlt = '';
              
              if (tab.id === 'breakdown-tab') {
                IconComponent = BreakdownIcon;
                iconAlt = t('tabBar.breakdownAndSets');
              } else if (tab.id === 'schedule-tab') {
                IconComponent = ScheduleIcon;
                iconAlt = t('tabBar.schedule');
              } else if (tab.id === 'element-manager-tab') {
                IconComponent = ItemIcon;
                iconAlt = t('tabBar.elementManager');
              }
              
              const breakdownFixedEnClass = (i18n.language || '').startsWith('en')
                ? ` fixed-tab--en-${tab.id}`
                : '';
              return (
              <div
                key={tab.id}
                  className={`tab fixed-tab ${isActive ? 'active' : ''}${breakdownFixedEnClass}`}
                onClick={() => handleBreakdownFixedTabClick(tab.id)}
              >
                <div className="tab-left"></div>
                <div className="tab-mid">
                  {IconComponent && (
                    <IconComponent className="tab-icon" aria-label={iconAlt} />
                  )}
                  <span className="tab-title">{tab.title}</span>
                </div>
                <div className="tab-right"></div>
              </div>
              );
            })}
          </div>
        )}
        {/* 如果路径是 /budgets 或 /budget 相关，显示预算固定标签 */}
        {!isHomePage && budgetId && (currentPath.startsWith('/budgets') || currentPath.startsWith('/budget/')) && (
          <div
            className={`tabs${(i18n.language || '').startsWith('en') ? ' tabs--budget-fixed-en' : ''}`}
          >
            {budgetFixedTabs.map(tab => {
              const isActive = budgetActiveTabId === tab.id;
              let IconComponent: React.FC<React.SVGProps<SVGSVGElement>> | null = null;
              let iconAlt = '';
              
              if (tab.id === 'budget-tab') {
                IconComponent = BudgetIcon;
                iconAlt = t('tabBar.budgetTable');
              } else if (tab.id === 'financial-tab') {
                IconComponent = FinancialIcon;
                iconAlt = t('tabBar.financial');
              } else if (tab.id === 'analysis-tab') {
                IconComponent = AnalysisIcon;
                iconAlt = t('tabBar.analysis');
              }
              
              const budgetFixedEnClass = (i18n.language || '').startsWith('en')
                ? ` fixed-tab--en-${tab.id}`
                : '';
              return (
              <div
                key={tab.id}
                  className={`tab fixed-tab ${isActive ? 'active' : ''}${budgetFixedEnClass}`}
                onClick={() => handleBudgetFixedTabClick(tab.id)}
              >
                <div className="tab-left"></div>
                <div className="tab-mid">
                  {IconComponent && (
                    <IconComponent className="tab-icon" aria-label={iconAlt} />
                  )}
                  <span className="tab-title">{tab.title}</span>
                </div>
                <div className="tab-right"></div>
              </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* 导出下拉菜单 */}
      {exportMenuVisible && (
        <ExportDialog
          visible={exportMenuVisible}
          onClose={() => setExportMenuVisible(false)}
          scenes={getCurrentBreakdownData().scenes}
          breakdownInfo={getCurrentBreakdownData().breakdownInfo}
          breakdownId={breakdownIdForTabs || undefined}
          position={getExportMenuPosition()}
        />
      )}

      {/* 查找替换弹窗 - 分场分景 */}
      {findReplaceVisible && activeTab === 'breakdown' && breakdownIdForTabs && (
        <div 
          className="find-replace-wrapper" 
          style={{
            '--position-top': `${findReplacePosition.top}px`,
            '--position-left': `${findReplacePosition.left}px`,
          } as React.CSSProperties}
        >
          <FindReplace
            visible={findReplaceVisible}
            onClose={handleCloseFindReplace}
            columnData={multiColumnData}
            columnIndex="multi-column"
            scenes={currentBreakdownData.scenes}
            onUpdate={handleFindReplaceUpdate}
            showRecommendMenu={false}
          />
        </div>
      )}

      {/* 查找替换弹窗 - 元素管理 */}
      {elementFindReplaceVisible && activeTab === 'element-manager' && breakdownIdForTabs && (
        <div 
          className="find-replace-wrapper" 
          style={{
            '--position-top': `${elementFindReplacePosition.top}px`,
            '--position-left': `${elementFindReplacePosition.left}px`,
          } as React.CSSProperties}
        >
          <FindReplace
            visible={elementFindReplaceVisible}
            onClose={handleCloseElementFindReplace}
            columnData={elementColumnData}
            columnIndex="elements"
            rowIdentifiers={elementRowIdentifiers}
            onUpdate={handleElementFindReplaceUpdate}
            showRecommendMenu={false}
          />
        </div>
      )}
    </div>
  );
});

export default TabBar;
